<template>
  <el-row v-loading.fullscreen.lock="fullScreenLoading">
    <el-col
      :sm="{ span: 14, offset: 5 }"
      :md="{ span: 12, offset: 6 }"
      :lg="{ span: 12, offset: 6 }"
    >
      <el-header
        height="140px"
        class="app-header pt-50 pb-30"
      >
        <Menu />
        <h3 class="app-header-title mt-0">
          {{ $t("profile.edit-profile") }}
        </h3>
      </el-header>
      <el-main>
        <el-form
          v-if="userProfile"
          :model="userProfile"
          :rules="getFormRules"
          ref="editUserForm"
          label-position="top"
        >
          <el-form-item
            :label="$t('profile.first-name')"
            prop="name"
          >
            <el-input v-model="userProfile.name" />
          </el-form-item>
          <el-form-item
            :label="$t('profile.last-name')"
            prop="familyName"
          >
            <el-input v-model="userProfile.familyName" />
          </el-form-item>
          <el-form-item
            :label="$t('profile.nick-name')"
            prop="nickName"
          >
            <el-input v-model="userProfile.nickName" />
          </el-form-item>
          <el-form-item
            :label="$t('profile.phone')"
            prop="phoneNumber"
          >
            <vue-tel-input
              v-model="userProfile.phoneNumber"
              @input="trimSpaces($event, 'phoneNumber')"
            />
          </el-form-item>
          <el-form-item
            :label="$t('profile.email')"
            prop="email"
          >
            <el-input
              v-model="userProfile.email"
              @input="trimSpaces($event, 'email')"
            />
          </el-form-item>
          <el-form-item
            :label="$t('profile.motivation')"
            prop="motivation"
          >
            <el-input v-model="userProfile.motivation" />
          </el-form-item>
          <el-form-item
            v-if="userProfile.onboarding === false"
            :label="$t('profile.e2n')"
            prop="mitarbeiterid"
          >
            <el-input
              v-mask="'##########'"
              v-model="userProfile.accReference"
            />
          </el-form-item>
          <el-form-item
            :label="$t('profile.notification-emails')"
            prop="sendPaymentEmail"
          >
            <el-radio-group v-model="userProfile.isSendEmailEnabled">
              <el-radio :label="true">
                {{ $t("yes") }}
              </el-radio>
              <el-radio :label="false">
                {{ $t("no") }}
              </el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item
            :label="$t('profile.iban')"
            prop="iban"
          >
            <el-row class="iban">
              <el-col :span="1">
                <el-tooltip
                  v-if="userProfile.ibanStatus === false"
                  :content="$t('profile.iban-contact-support')"
                  placement="top"
                >
                  <i class="el-icon-circle-close" />
                </el-tooltip>
                <el-tooltip
                  v-else
                  :content="$t('profile.iban-connected')"
                  placement="top"
                >
                  <i class="el-icon-circle-check" />
                </el-tooltip>
              </el-col>
              <el-col
                :offset="1"
                :span="22"
              >
                <el-input
                  v-model="userProfile.iban"
                  @change="getCountryFromIban"
                  @input="trimSpaces($event, 'iban')"
                />
              </el-col>
            </el-row>
          </el-form-item>
          <el-row v-if="validIban">
            <el-form-item
              :label="$t('profile.address')"
              prop="address"
            >
              <el-input v-model="userProfile.address" />
            </el-form-item>
            <el-form-item
              :label="$t('profile.postal_code')"
              prop="postalCode"
            >
              <div class="el-input">
                <the-mask
                  class="el-input__inner"
                  :mask="'#####'"
                  v-model="userProfile.postalCode"
                />
              </div>
            </el-form-item>
            <el-form-item
              :label="$t('profile.city')"
              prop="city"
            >
              <el-input v-model="userProfile.city" />
            </el-form-item>
            <el-form-item :label="$t('profile.country')">
              <el-select v-model="userProfile.country">
                <el-option
                  v-for="(country, code) in countries"
                  :key="code"
                  :label="country"
                  :value="code"
                />
              </el-select>
            </el-form-item>
            <el-form-item
              :label="$t('profile.dob')"
              prop="birthdate"
            >
              <el-date-picker
                v-model="userProfile.birthdate"
                format="yyyy-MM-dd"
                value-format="yyyy-MM-dd"
                type="date"
                :picker-options="dobPickerOptions"
              />
            </el-form-item>
            <el-form-item
              :label="$t('profile.send-tips-sms')"
              prop="disableTipsSms"
            >
              <el-radio-group v-model="userProfile.tipsSmsDisabled">
                <el-radio :label="false">
                  {{ $t("yes") }}
                </el-radio>
                <el-radio :label="true">
                  {{ $t("no") }}
                </el-radio>
              </el-radio-group>
            </el-form-item>
            <el-form-item
              v-if="isAdmin"
              :label="$t('profile.disable-payout')"
              prop="disableIbanTransfer"
            >
              <el-switch v-model="userProfile.disableIbanTransfer" />
            </el-form-item>
            <el-form-item
              :label="$t('re-conciliation.check-payout-threshold')"
              prop="checkPayoutThreshold"
            >
              <el-switch v-model="userProfile.checkPayoutThreshold" />
            </el-form-item>
          </el-row>
          <el-form-item :label="$t('profile.payout-provider')">
            <el-select v-model="userProfile.payoutPsp">
              <el-option
                v-for="(provider, code) in payoutProviders"
                :key="code"
                :label="provider"
                :value="code"
              />
            </el-select>
          </el-form-item>
          <el-form-item class="mt-20">
            <el-row>
              <el-col :span="11">
                <router-link
                  :to="{ name: 'User' }"
                  custom
                  v-slot="{ navigate }"
                >
                  <el-button
                    class="cancel-button"
                    @click="navigate"
                  >
                    {{ $t("form.cancel") }}
                  </el-button>
                </router-link>
              </el-col>
              <el-col
                :offset="2"
                :span="11"
              >
                <el-button
                  class="primary-button"
                  type="primary"
                  @click="submitForm('editUserForm')"
                >
                  {{ $t("form.save") }}
                </el-button>
              </el-col>
            </el-row>
          </el-form-item>
        </el-form>
      </el-main>
    </el-col>
  </el-row>
</template>

<script>
import Menu from "../Menu/Menu";
import IBAN from "iban";
import authHelpers from "@/plugins/auth-helpers";
import countriesList from "../../assets/countries.json";
import { jsonp } from "vue-jsonp";
import { usersAPI } from "../../shared/services/users.service";
import normalazieUser from "../../shared/normalizeUser";
import updateUserCognitoNormalizer from "../../shared/updateUserCognitoNormalizer";
import payoutProviders from "../../shared/payout-providers";

export default {
  name: "EditUser",
  components: {
    Menu,
  },
  data: () => ({
    isAdmin: false,
    fullScreenLoading: true,
    userProfile: null,
    dobPickerOptions: {
      disabledDate(date) {
        return date > new Date();
      },
    },
    validIban: false,
  }),
  computed: {
    payoutProviders() {
      return payoutProviders
    },
    getFormRules: function() {
      const validateIban = (rule, value, callback) => {
        if (value === "") {
          this.validIban = false;
          callback();
        } else if (!IBAN.isValid(value)) {
          this.validIban = false;
          callback(
            new Error(
              this.$t("form.validation.format", {
                field: this.$t("profile.iban"),
              })
            )
          );
        } else {
          this.validIban = !(value === "" || !IBAN.isValid(value));
          callback();
        }
      };
      const ibanApiChecker = (rule, value, callback) => {
        if (value === "") {
          callback();
        } else {
          jsonp(
            `https://api.ibanapi.com/v1/validate/${value}?api_key=${process.env.VUE_APP_IBAN_API_KEY}`
          )
            .then(() => {
              callback();
            })
            .catch(() => {
              callback(
                new Error(
                  this.$t("form.validation.incorrect", {
                    field: this.$t("profile.iban"),
                  })
                )
              );
            });
        }
      };
      return {
        name: [
          {
            required: true,
            message: this.$t("form.validation.required", {
              field: this.$t("profile.first-name"),
            }),
            trigger: "blur",
          },
        ],
        familyName: [
          {
            required: true,
            message: this.$t("form.validation.required", {
              field: this.$t("profile.last-name"),
            }),
            trigger: "blur",
          },
        ],
        phoneNumber: [
          {
            required: true,
            message: this.$t("form.validation.required", {
              field: this.$t("profile.phone"),
            }),
            trigger: "blur",
          },
        ],
        email: [
          {
            type: "email",
            message: this.$t("form.validation.format", {
              field: this.$t("profile.email"),
            }),
            trigger: "blur",
          },
          {
            required: true,
            message: this.$t("form.validation.required", {
              field: this.$t("profile.email"),
            }),
            trigger: "blur",
          },
        ],
        iban: [
          { validator: validateIban, trigger: "change" },
          { validator: ibanApiChecker, trigger: "submit" },
        ],
        birthdate: [
          {
            required: true,
            message: this.$t("form.validation.required", {
              field: this.$t("profile.dob"),
            }),
            trigger: "blur",
          },
        ],
        address: [
          {
            required: true,
            message: this.$t("form.validation.required", {
              field: this.$t("profile.address"),
            }),
            trigger: "blur",
          },
        ],
        city: [
          {
            required: true,
            message: this.$t("form.validation.required", {
              field: this.$t("profile.city"),
            }),
            trigger: "blur",
          },
        ],
        postalCode: [
          {
            required: true,
            message: this.$t("form.validation.required", {
              field: this.$t("profile.postal_code"),
            }),
            trigger: "blur",
          },
        ],
      };
    },
    countries: function() {
      return countriesList;
    },
  },
  async created() {
    authHelpers.isCurrentUserAdmin().then((response) => {
      this.isAdmin = response;
    });

    const [error, user] = await usersAPI.getUser(this.$route.params.userId);
    this.fullScreenLoading = false;
    if (error) {
      this.$message.error(this.$t("form.something-wrong"));
      return;
    }

    this.userProfile = normalazieUser(user);

    if (this.userProfile.iban.length) {
      this.validIban = true;
    }
  },
  methods: {
    trimSpaces(value = "", field) {
      this.userProfile[field] = value.replace(/\s/g, "");
    },
    submitForm(formName) {
      this.$refs[formName].validate((valid) => valid && this.saveUserData());
    },
    async saveUserData() {
      this.fullScreenLoading = true;
      const [cognitoError] = await usersAPI.updateCognitoUser(
        updateUserCognitoNormalizer(this.userProfile),
        this.userProfile.username
      );

      if (cognitoError) {
        this.fullScreenLoading = false;
        this.$message.error(this.$t("form.something-wrong"));
        return;
      }

      const [error, response] = await usersAPI.updateUser(
        {
          oldUserUpdatedRequest: this.userProfile,
          newUserUpdatedRequest: this.userProfile,
        },
        this.userProfile.username
      );

      if (error) {
        this.fullScreenLoading = false;
        this.$message.error(this.$t("form.something-wrong"));
        return;
      }

      if (response.payload.connectedAccountStatus) {
        if (response.payload.accReference) {
          const [cognitoError] = await usersAPI.updateCognitoUser(
            [
              {
                attributeName: "custom:acc_reference",
                attributeValue: response.payload.accReference,
              },
            ],
            this.userProfile.username
          );

          if (cognitoError) {
            this.fullScreenLoading = false;
            this.$message.error(this.$t("form.something-wrong"));
            return;
          }

          this.$message.success(
            this.$t("form.item-changed", { item: this.$t("profile.title") })
          );
          this.$router.push({ name: "User" });
        }
      } else {
        this.$message.warning(this.$t("profile.iban-contact-support"));
      }

      this.fullScreenLoading = false;
    },
    getCountryFromIban(value) {
      this.userProfile.country = value
        ? this.userProfile.iban.substring(0, 2)
        : "DE";
    },
  },
};
</script>

<style scoped lang="scss">
.iban {
  display: flex;
  align-items: center;

  i {
    font-size: 24px;
  }

  .el-icon-circle-close {
    color: #f56c6c;
  }

  .el-icon-circle-check {
    color: #1f9f43;
  }
}
</style>
